import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { 
  collection, addDoc, onSnapshot, query, 
  where, doc, updateDoc, deleteDoc, Timestamp 
} from 'firebase/firestore';
import Swal from 'sweetalert2';

function Vendormgmt() {
  const [vendors, setVendors] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // Form State
  const [formData, setFormData] = useState({ 
    name: '', contact: '', gst: '', address: '', openingBalance: 0 
  });
  const [editId, setEditId] = useState(null);

  const userId = auth.currentUser?.uid;
  const Toast = Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });

  // 1. Fetch Vendors
  useEffect(() => {
    if (!userId) return;
    const q = query(collection(db, "vendors"), where("userId", "==", userId));
    const unsub = onSnapshot(q, (snap) => {
      setVendors(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [userId]);

  // 2. Handle Add/Update
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { 
        ...formData, 
        openingBalance: parseFloat(formData.openingBalance) || 0,
        updatedAt: Timestamp.now() 
      };

      if (editId) {
        await updateDoc(doc(db, "vendors", editId), payload);
        Toast.fire({ icon: 'success', title: 'Vendor Updated' });
      } else {
        // When adding new, currentBalance starts as openingBalance
        await addDoc(collection(db, "vendors"), { 
          ...payload, 
          currentBalance: payload.openingBalance,
          userId, 
          createdAt: Timestamp.now() 
        });
        Toast.fire({ icon: 'success', title: 'Vendor Added' });
      }
      resetForm();
      // eslint-disable-next-line no-undef
      $('#vendorModal').modal('hide');
    } catch (err) {
      Toast.fire({ icon: 'error', title: err.message });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', contact: '', gst: '', address: '', openingBalance: 0 });
    setEditId(null);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({ 
        title: 'Delete Vendor?', 
        text: "All associated purchase history might be affected!", 
        icon: 'warning', 
        showCancelButton: true,
        confirmButtonColor: '#d33'
    });
    if (result.isConfirmed) {
      await deleteDoc(doc(db, "vendors", id));
      Toast.fire({ icon: 'success', title: 'Deleted' });
    }
  };

  // 3. Search & Pagination Logic
  const filteredVendors = vendors.filter(v => 
    v.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    v.gst?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredVendors.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredVendors.length / itemsPerPage);

  return (
    <div className="content-wrapper bg-light min-vh-100">
      <section className="content-header py-3 px-4">
        <div className="d-flex justify-content-between align-items-center bg-white p-3 shadow-sm rounded border-left-blue">
          <h4 className="m-0 font-weight-bold text-navy"><i className="fas fa-address-book mr-2"></i>Vendor Management</h4>
          <button className="btn btn-navy btn-sm shadow-sm" onClick={resetForm} data-toggle="modal" data-target="#vendorModal">
            <i className="fas fa-plus-circle mr-1"></i> Register New Vendor
          </button>
        </div>
      </section>

      <section className="content px-4">
        <div className="card border-0 shadow-sm rounded-lg">
          <div className="card-header bg-white py-3">
            <div className="row">
              <div className="col-md-4">
                <div className="input-group input-group-sm">
                  <div className="input-group-prepend"><span className="input-group-text bg-light border-right-0"><i className="fas fa-search"></i></span></div>
                  <input type="text" className="form-control border-left-0 bg-light" placeholder="Search by name or GSTIN..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0 text-sm">
                <thead className="bg-light text-uppercase">
                  <tr>
                    <th className="text-center" width="60">Sr.</th>
                    <th>Vendor Name</th>
                    <th>GST Number</th>
                    <th>Contact</th>
                    <th className="text-right">Balance Due (₹)</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? currentItems.map((v, index) => (
                    <tr key={v.id}>
                      <td className="text-center text-muted font-weight-bold">{indexOfFirstItem + index + 1}</td>
                      <td>
                        <div className="font-weight-bold text-navy">{v.name}</div>
                        <small className="text-muted">{v.address || 'No Address'}</small>
                      </td>
                      <td><span className="badge badge-light border px-2">{v.gst || 'N/A'}</span></td>
                      <td><i className="fas fa-phone-alt mr-2 text-xs text-muted"></i>{v.contact}</td>
                      <td className="text-right">
                        <span className={`font-weight-bold ${v.currentBalance > 0 ? 'text-danger' : 'text-success'}`}>
                          ₹{(v.currentBalance || 0).toLocaleString()}
                        </span>
                      </td>
                      <td className="text-center">
                        <button className="btn btn-link btn-sm text-info mr-2" onClick={() => { setEditId(v.id); setFormData(v); }} data-toggle="modal" data-target="#vendorModal"><i className="fas fa-edit"></i></button>
                        <button className="btn btn-link btn-sm text-danger" onClick={() => handleDelete(v.id)}><i className="fas fa-trash-alt"></i></button>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="6" className="text-center py-5 text-muted">No vendors found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          
          <div className="card-footer bg-white d-flex justify-content-between align-items-center py-2">
            <small className="text-muted font-italic">Page {currentPage} of {totalPages || 1}</small>
            <ul className="pagination pagination-sm m-0">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}><button className="page-link" onClick={() => setCurrentPage(prev => prev - 1)}>Prev</button></li>
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}><button className="page-link" onClick={() => setCurrentPage(prev => prev + 1)}>Next</button></li>
            </ul>
          </div>
        </div>
      </section>

      {/* VENDOR MODAL */}
      <div className="modal fade" id="vendorModal" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content border-0">
            <div className="modal-header bg-navy text-white py-2">
              <h6 className="modal-title font-weight-bold">{editId ? 'Edit Vendor Details' : 'Register Vendor'}</h6>
              <button type="button" className="close text-white" data-dismiss="modal"><span>&times;</span></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body p-4">
                <div className="form-group">
                  <label className="text-xs font-weight-bold">Vendor/Company Name</label>
                  <input type="text" className="form-control" placeholder="Enter name" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required />
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <div className="form-group">
                            <label className="text-xs font-weight-bold">GSTIN (Optional)</label>
                            <input type="text" className="form-control text-uppercase" placeholder="27XXXXX" value={formData.gst} onChange={e => setFormData({...formData, gst: e.target.value})} />
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group">
                            <label className="text-xs font-weight-bold">Contact Number</label>
                            <input type="text" className="form-control" placeholder="Mobile" value={formData.contact} onChange={e => setFormData({...formData, contact: e.target.value})} required />
                        </div>
                    </div>
                </div>
                <div className="form-group">
                    <label className="text-xs font-weight-bold">Full Address</label>
                    <textarea className="form-control" rows="2" value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})}></textarea>
                </div>
                {!editId && (
                    <div className="form-group">
                        <label className="text-xs font-weight-bold">Opening Balance (₹)</label>
                        <input type="number" className="form-control bg-light-yellow" value={formData.openingBalance} onChange={e => setFormData({...formData, openingBalance: e.target.value})} />
                        <small className="text-muted text-xs">Amount you already owe this vendor.</small>
                    </div>
                )}
              </div>
              <div className="modal-footer bg-light py-2">
                <button type="button" className="btn btn-secondary btn-sm" data-dismiss="modal">Cancel</button>
                <button type="submit" disabled={loading} className="btn btn-navy btn-sm px-4 shadow-sm">
                  {loading ? 'Processing...' : 'Save Vendor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .bg-navy { background-color: #001f3f !important; }
        .text-navy { color: #001f3f !important; }
        .btn-navy { background-color: #001f3f; color: #fff; }
        .btn-navy:hover { background-color: #001329; color: #fff; }
        .border-left-blue { border-left: 4px solid #001f3f; }
        .bg-light-yellow { background-color: #fffdf0; }
        .text-xs { font-size: 0.75rem; }
      `}} />
    </div>
  );
}

export default Vendormgmt;